import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Home, Building2, Users, Bed, User, MapPin, Eye, Edit, Trash2, MoreHorizontal, ArrowLeft, Shirt, Package, ClipboardList, Warehouse } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const HospedajesTable = ({ 
  hospedajes = [], 
  edificio = null,
  isLoading = false, 
  onView, 
  onEdit, 
  onDelete,
  onBack
}) => {
  const navigate = useNavigate();
  const getTipoIcon = (tipo) => {
    switch (tipo?.toLowerCase()) {
      case 'horizontal':
        return <Home className="h-4 w-4 text-blue-600" />;
      case 'vertical':
        return <Building2 className="h-4 w-4 text-green-600" />;
      default:
        return <Home className="h-4 w-4 text-gray-600" />;
    }
  };

  const getTipoLabel = (tipo) => {
    switch (tipo?.toLowerCase()) {
      case 'horizontal':
        return 'Horizontal';
      case 'vertical':
        return 'Vertical';
      default:
        return tipo || 'No especificado';
    }
  };

  const getTipoRentaLabel = (tipoRenta) => {
    switch (tipoRenta?.toLowerCase()) {
      case 'renta corta':
        return 'Renta Corta';
      case 'renta larga':
        return 'Renta Larga';
      default:
        return tipoRenta || 'No especificado';
    }
  };

  const getTipoHabitacionLabel = (tipoHabitacion) => {
    switch (tipoHabitacion?.toLowerCase()) {
      case 'entire_home':
        return 'Casa/Apartamento completo';
      case 'private_room':
        return 'Habitación privada';
      case 'shared_room':
        return 'Habitación compartida';
      default:
        return tipoHabitacion || 'No especificado';
    }
  };

  const getStatusBadge = (estado) => {
    const variant = estado === 'Activo' ? 'default' : 'secondary';
    const className = estado === 'Activo' 
      ? 'bg-green-100 text-green-800 hover:bg-green-200' 
      : 'bg-gray-100 text-gray-800 hover:bg-gray-200';
    
    return (
      <Badge variant={variant} className={`text-xs ${className}`}>
        {estado}
      </Badge>
    );
  };

  const getGestionBadge = (gestion, tipo) => {
    const colors = {
      'Autonoma': 'bg-blue-100 text-blue-800',
      'Directa': 'bg-purple-100 text-purple-800',
      'Tercerizada': 'bg-orange-100 text-orange-800'
    };
    
    return (
      <Badge className={`text-xs ${colors[gestion] || 'bg-gray-100 text-gray-800'}`}>
        {tipo}: {gestion}
      </Badge>
    );
  };

  const handleAction = (action, hospedaje) => {
    switch (action) {
      case 'view':
        onView?.(hospedaje);
        break;
      case 'edit':
        onEdit?.(hospedaje);
        break;
      case 'delete':
        onDelete?.(hospedaje);
        break;
      default:
        console.warn('Acción no reconocida:', action);
    }
  };

  // Handlers para acciones rápidas
  const handleSolicitarLenceria = () => {
    // Navegar con el ID del edificio en la ruta y pasar datos completos por state
    navigate(`/solicitudes/lenceria/${edificio._id}`, { 
      state: { 
        edificio: {
          id: edificio._id,
          nombre: edificio.nombre,
          ciudad: edificio.ciudad,
          direccion: edificio.direccion,
          tipoLogistica: edificio.tipoLogistica
        }
      } 
    });
  };

  const handleSolicitarInsumos = () => {
    // Navegar con el ID del edificio en la ruta y pasar datos completos por state
    navigate(`/solicitudes/insumos/${edificio._id}`, { 
      state: { 
        edificio: {
          id: edificio._id,
          nombre: edificio.nombre,
          ciudad: edificio.ciudad,
          direccion: edificio.direccion,
          tipoLogistica: edificio.tipoLogistica
        }
      } 
    });
  };

  const handleInventarioVertical = () => {
    // Navegar al inventario vertical del edificio
    navigate(`/lavanderia/inventario-vertical/${edificio._id}`, { 
      state: { 
        edificio: {
          _id: edificio._id,
          nombre: edificio.nombre,
          ciudad: edificio.ciudad,
          direccion: edificio.direccion,
          tipoLogistica: edificio.tipoLogistica
        },
        fromHospedajes: true // Flag para indicar que viene desde HospedajesTable
      } 
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Header skeleton */}
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
        
        {/* Table skeleton */}
        <div className="bg-white rounded-lg border">
          <div className="animate-pulse">
            <div className="h-12 bg-gray-100 rounded-t-lg"></div>
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-50 border-t border-gray-100"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with building info */}
      <Card className="card-gradient-bg shadow-lg">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={onBack}
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Volver a Portafolios</span>
              </Button>
            </div>
          </div>
          
          {edificio && (
            <div className="flex items-center space-x-4 pt-4 border-t">
              <Building2 className="h-8 w-8 text-foreground" />
              <div>
                <CardTitle className="text-2xl font-bold text-foreground">
                   {edificio.nombre}
                </CardTitle>
                <CardDescription className="text-muted-foreground flex items-center space-x-4">
                  <span className="flex items-center space-x-1">
                    <MapPin className="h-4 w-4" />
                    <span>{edificio.ciudad} {edificio.direccion}</span>
                  </span>
                  <span>{hospedajes.length} activos encontrados</span>
                </CardDescription>
              </div>
            </div>
          )}
        </CardHeader>
      </Card>

      {/* Acciones Rápidas - Solo para Logística Vertical */}
      {edificio?.tipoLogistica === 'vertical' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Acciones Rápidas</CardTitle>
            <CardDescription>Gestiona operaciones comunes para este portafolio vertical</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

              {/* Solicitar Lencería */}
              <Button
                variant="outline"
                className="h-auto flex flex-col items-center justify-center p-6 space-y-3 hover:bg-blue-50 hover:border-blue-300 transition-colors"
                onClick={handleSolicitarLenceria}
              >
                <div className="p-3 bg-blue-100 rounded-full">
                  <Shirt className="h-6 w-6 text-blue-600" />
                </div>
                <div className="text-center">
                  <div className="font-semibold text-gray-900">Solicitar Recolección</div>
                  <div className="text-xs text-gray-500 mt-1">Crear solicitud de recolección</div>
                </div>
              </Button>

              {/* Solicitar Insumos Operativos */}
              <Button
                variant="outline"
                className="h-auto flex flex-col items-center justify-center p-6 space-y-3 hover:bg-purple-50 hover:border-purple-300 transition-colors"
                onClick={handleSolicitarInsumos}
              >
                <div className="p-3 bg-purple-100 rounded-full">
                  <Package className="h-6 w-6 text-purple-600" />
                </div>
                <div className="text-center">
                  <div className="font-semibold text-gray-900">Solicitar Insumos</div>
                  <div className="text-xs text-gray-500 mt-1">Insumos operativos</div>
                </div>
              </Button>
              {/* Inventario Vertical */}
              <Button
                variant="outline"
                className="h-auto flex flex-col items-center justify-center p-6 space-y-3 hover:bg-green-50 hover:border-green-300 transition-colors"
                onClick={handleInventarioVertical}
              >
                <div className="p-3 bg-green-100 rounded-full">
                  <Warehouse className="h-6 w-6 text-green-600" />
                </div>
                <div className="text-center">
                  <div className="font-semibold text-gray-900">Prendas en lavandería</div>
                  <div className="text-xs text-gray-500 mt-1">Ver inventario y movimientos</div>
                </div>
              </Button>

            </div>
          </CardContent>
        </Card>
      )}

      {/* Hospedajes Table */}
      { edificio?.tipoLogistica === 'vertical' || hospedajes.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Home className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium text-muted-foreground mb-2">
              No se encontraron activos
            </p>
            <p className="text-sm text-muted-foreground text-center">
              Esta propiedad no tiene activos asociados
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
          {/* Header */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-700">
              <div className="col-span-3">Activo</div>
              <div className="col-span-2">Tipo</div>
              <div className="col-span-2">Coordinador</div>
              <div className="col-span-2">Gestión</div>
              <div className="col-span-2">Capacidad</div>
            </div>
          </div>

          {/* Body */}
          <div className="divide-y divide-gray-200">
            <AnimatePresence>
              {hospedajes.map((hospedaje, index) => (
                <motion.div
                  key={hospedaje._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.2, delay: index * 0.02 }}
                  className="px-6 py-4 hover:bg-gray-50 transition-colors duration-200 cursor-pointer"
                  onClick={() => navigate(`/hospedaje/${hospedaje._id}`, { state: { hospedaje } })}
                >
                  <div className="grid grid-cols-12 gap-4 items-center">
                    {/* Hospedaje */}
                    <div className="col-span-3">
                      <div className="flex items-center space-x-3">
                        {getTipoIcon(hospedaje.tipo)}
                        <div>
                          <div className="font-medium text-gray-900">
                            {hospedaje.nombrePropiedad}
                          </div>
                          <div className="flex items-center space-x-2 mt-1">
                            {getStatusBadge(hospedaje.estado)}
                            <Badge variant="outline" className="text-xs">
                              {getTipoRentaLabel(hospedaje.tipoRenta)}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Tipo */}
                    <div className="col-span-2">
                      <div className="space-y-1">
                        <span className="text-sm font-medium text-gray-900">
                          {getTipoLabel(hospedaje.tipo)}
                        </span>
                        <div className="text-xs text-gray-500">
                          {getTipoHabitacionLabel(hospedaje.tipoHabitacion)}
                        </div>
                      </div>
                    </div>

                    {/* Coordinador */}
                    <div className="col-span-2">
                      <div className="flex items-center space-x-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-900">
                          {hospedaje.coordinador?.nombre || 'Sin asignar'}
                        </span>
                      </div>
                    </div>

                    {/* Gestión */}
                    <div className="col-span-2">
                      <div className="space-y-1">
                        {getGestionBadge(hospedaje.gestionLenceria, 'Lencería')}
                        {getGestionBadge(hospedaje.gestionAseo, 'Aseo')}
                      </div>
                    </div>

                    {/* Capacidad */}
                    <div className="col-span-2">
                      <div className="space-y-1">
                        <div className="flex items-center space-x-1 text-sm">
                          <Home className="h-3 w-3 text-gray-400" />
                          <span>{hospedaje.numeroHabitaciones || 0} hab.</span>
                        </div>
                        <div className="flex items-center space-x-1 text-sm">
                          <Bed className="h-3 w-3 text-gray-400" />
                          <span>{hospedaje.numeroCamas || 0} camas</span>
                        </div>
                      </div>
                    </div>

                    {/* Acciones */}
                    {/* <div className="col-span-1 text-center" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 hover:bg-gray-100"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                            <span className="sr-only">Abrir menú</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAction('view', hospedaje);
                            }}
                            className="flex items-center space-x-2 cursor-pointer"
                          >
                            <Eye className="h-4 w-4" />
                            <span>Ver detalles</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAction('edit', hospedaje);
                            }}
                            className="flex items-center space-x-2 cursor-pointer"
                          >
                            <Edit className="h-4 w-4" />
                            <span>Editar</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAction('delete', hospedaje);
                            }}
                            className="flex items-center space-x-2 cursor-pointer text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                            <span>Eliminar</span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>*/}
                  </div>
                </motion.div> 
              ))}
            </AnimatePresence>
          </div>

          {/* Footer info */}
          <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              Mostrando {hospedajes.length} hospedajes
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HospedajesTable;
